import { getGuid } from 'owa-guid';
import { composeStore } from 'owa-mail-compose-store';
import { addPopoutV2 } from 'owa-popout-v2';
import WindowFeaturesOptions from 'owa-popout-v2/lib/store/schema/WindowFeaturesOptions';
import {
    cloneSxSStore,
    getOrCreateSxSStoreData,
    onComposeStateChange,
    onCustomizationChange,
    onSxSReadingPaneChange,
    SxSCustomization,
    SXSV2_CONTAINER_ID,
    lazyRemoveSxSViewFromDOM,
} from 'owa-sxs-store';
import { getStore, TabViewState } from 'owa-tab-store';
import removeTabViewState from 'owa-tab-store/lib/actions/removeTabViewState';
import { TabType } from 'owa-tab-store/lib/store/schema/TabViewState';
import addTab from 'owa-tab-store/lib/utils/addTab';
import ensureSxSTabHandler from './SxSTabHandler';

const SXSID_PREFIX = 'SXSID_';
const DEFAULT_POPOUT_WIDTH = 1920;

function hideRightHandSide(sxsId: string) {
    onComposeStateChange(null, sxsId);
    onSxSReadingPaneChange(null, sxsId);
}

function cloneSxS(sxsId: string): string {
    const clonedSxSId = SXSID_PREFIX + getGuid();
    cloneSxSStore(sxsId, clonedSxSId);
    return clonedSxSId;
}

/**
 * Popout SxS to a new window
 * @param sxsId The sxsId that is used to display sxs view
 * @param parentWindow The original window it pops out from
 */
export default function popOutSxS(sxsId: string, parentWindow: Window): boolean {
    let newSxSId = sxsId;

    const sxsStore = getOrCreateSxSStoreData(sxsId);
    const composeViewState = sxsStore.composeId && composeStore.viewStates.get(sxsStore.composeId);

    const store = getStore();
    let originalTab: TabViewState | null = null;
    for (const tab of store.tabs) {
        if (tab.sxsId === sxsId) {
            originalTab = tab;
            break;
        }
    }
    const popOutFromComposeTab: boolean = originalTab?.type === TabType.Compose;

    const options: Partial<WindowFeaturesOptions> = {};
    // pop out the same SxS
    if (composeViewState && popOutFromComposeTab) {
        options.width =
            parentWindow?.document?.getElementById(SXSV2_CONTAINER_ID)?.getBoundingClientRect()
                ?.width ?? DEFAULT_POPOUT_WIDTH;
        lazyRemoveSxSViewFromDOM.importAndExecute(parentWindow);
        if (originalTab) {
            removeTabViewState(originalTab);
        }
        ensureSxSTabHandler();
    }
    // pop out SxS only with preview pane
    else {
        newSxSId = cloneSxS(sxsId);
        hideRightHandSide(newSxSId);
    }

    onCustomizationChange(SxSCustomization.PopOut, newSxSId);

    const tabId = addTab(TabType.SxS, false /* isShown */, false /* makeActive */, newSxSId);
    addPopoutV2(
        'mail',
        'sxs',
        {
            deeplinkData: () => {},
            projectionTabId: tabId,
            projectionParentWindow: parentWindow,
        },
        options
    );

    return !(composeViewState && popOutFromComposeTab);
}
